import * as Yup from "yup";
import { Modal } from "flowbite-react";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import TextAreaFormik from "@/common/components/form/TextAreaFormik";
import InputMoney from "@/common/components/form/CurrencyInputFormik";
import InputFormik from "@/common/components/form/InputFormik";
import ComboBoxFormik from "@/common/components/form/ComboBoxFormik";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { getPartnersFiltered } from "@/common/services/partners.api";
import { PartnerInfoType } from "@/types/vehicle.type";
import { createdBillingB2B } from "@/common/services/company-b2b.api";

type Props = {
  companyId?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const validationSchema = Yup.object().shape({
  value: Yup.string()
    .required("Valor da cobrança é obrigatório")
    .test(
      "greater-than-zero",
      "O valor deve ser maior que 0,00",
      (value) => {
        if (!value) return false;
        const numericValue = parseFloat(
          value.replace(/\./g, "").replace(",", ".")
        );
        return numericValue > 0;
      }
    ),
  description: Yup.string().required("Descrição da cobrança é obrigatória"),
  partnerId: Yup.string().required("Parceiro é obrigatório"),
  dueDate: Yup.string()
    .required("Data de vencimento é obrigatória")
    .test(
      "future-date",
      "A data não pode ser anterior à data atual",
      (value) => {
        if (!value) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inputDate = new Date(value + "T00:00:00");
        inputDate.setHours(0, 0, 0, 0);
        return inputDate.getTime() >= today.getTime();
      }
    ),
});

export function ModalAddBillingB2B(props: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partners, setPartners] = useState<PartnerInfoType[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const { notifyError, notifySuccess } = useToastContext();

  const handleCloseModal = () => {
    props.onClose && props.onClose();
  };

  const initialValues = {
    id: props.companyId || "",
    partnerId: "",
    value: 0,
    dueDate: "",
    description: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!props.companyId) {
        notifyError("ID da empresa não encontrado");
        return;
      }

      try {
        setIsSubmitting(true);

        const billingData = {
          id: values.id,
          partnerId: values.partnerId,
          value: values.value,
          dueDate: values.dueDate,
          description: values.description,
        };

        const result = await createdBillingB2B(props.companyId, billingData);

        if (!result.ok) {
          notifyError("Erro ao criar cobrança");
          return;
        }

        notifySuccess("Cobrança criada com sucesso!");

        if (props.onSuccess) {
          props.onSuccess();
        } else {
          handleCloseModal();
        }
      } catch (error) {
        console.error("Erro ao criar cobrança:", error);
        notifyError("Erro ao criar cobrança");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    async function loadPartners() {
      setLoadingPartners(true);
      try {
        const result = await getPartnersFiltered({
          companyId: props.companyId,
          itemsPerPage: 100,
        });

        if (result.ok && result.data) {
          setPartners(result.data);
        }
      } catch (error) {
        console.error("Erro ao carregar parceiros:", error);
        notifyError("Erro ao carregar parceiros");
      } finally {
        setLoadingPartners(false);
      }
    }

    loadPartners();
  }, [props.companyId, notifyError]);

  const partnerOptions = useMemo(() => {
    return [
      { label: "-", value: "" },
      ...partners.map((partner) => ({
        label: partner.companyName || "-",
        value: partner._id || "",
      })),
    ];
  }, [partners]);

  return (
    <Modal size="3xl" show={true} onClose={handleCloseModal}>
      <Modal.Header className="text-black">
        <div className="flex items-baseline justify-center bg-gray-100">
          <h2 className="ml-2 text-black">Adicionar Cobrança B2B</h2>
        </div>
      </Modal.Header>
      <Modal.Body className="dark:text-black">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <InputMoney
                label="Valor da cobrança"
                name="value"
                required={true}
              />

              <ComboBoxFormik
                name="partnerId"
                label="Parceiro"
                options={partnerOptions}
                required={true}
                disabled={loadingPartners}
              />

              <InputFormik
                label="Data de vencimento"
                name="dueDate"
                type="date"
                required={true}
              />
            </div>

            <TextAreaFormik
              className="mt-4"
              label="Descrição da cobrança"
              name="description"
              required={true}
              placeholder="Descreva a cobrança"
            />

            <div className="flex justify-end mt-6">
              <ButtonWithLoader
                type="submit"
                isLoading={isSubmitting}
                disabled={!formik.isValid || isSubmitting}
              >
                Criar Cobrança
              </ButtonWithLoader>
            </div>
          </form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
}
